// src/utils/formatters.js

/**
 * Format currency values consistently
 * @param {string|number} value - The value to format
 * @param {string} currency - Currency symbol (default: 'UGX')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = 'UGX') => {
  const numValue = parseFloat(value || 0);
  return `${currency} ${numValue.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;
};

/**
 * Parse string or number to float safely
 * @param {string|number} value - The value to parse
 * @returns {number} Parsed number or 0 if invalid
 */
export const parseNumber = (value) => {
  const parsed = parseFloat(value || 0);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Calculate percentage safely
 * @param {number} part - The part value
 * @param {number} total - The total value
 * @returns {number} Percentage (0-100)
 */
export const calculatePercentage = (part, total) => {
  if (!total || total === 0) return 0;
  return Math.round((part / total) * 100);
};

/**
 * Accounting-style money formatting for financial statements/reports: two
 * decimal places always shown, negatives in parentheses (not a leading
 * minus), blank em-dash for null/undefined/NaN. Deliberately kept separate
 * from formatCurrency above (0 decimals, used across fee cards/dashboards)
 * so ledger report layouts don't change the formatting anywhere else.
 */
export const formatAccountingAmount = (value, { currency = 'UGX', blankZero = false } = {}) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return '—';
  if (blankZero && Math.abs(num) < 0.005) return '—';
  const abs = Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatted = `${currency} ${abs}`;
  return num < 0 ? `(${formatted})` : formatted;
};

/** CSV export for a flat array of row objects - no library needed. */
export const downloadCsv = (filename, headers, rows) => {
  const escape = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(escape).join(',')];
  rows.forEach((row) => lines.push(row.map(escape).join(',')));
  // UTF-8 BOM so Excel opens accented/symbol characters correctly.
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};